import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, Select, message, Table, InputNumber, Input, Button, Modal } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import type { TableRowSelection, FilterValue, SorterResult } from 'antd/es/table/interface';
import { SearchOutlined, CopyOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import type { ColumnType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../theme';

const { Option } = Select;
const { confirm } = Modal;

interface Transportista {
  id: number;
  nc: string;
  denominacion: string;
}

interface Ticket {
  id: number;
  numero_ticket: string;
  fecha_ticket: string;
  ingreso_egreso: string;
  cliente_nombre: string;
  productor_nombre: string;
  chofer_nombre: string;
  camion_patente: string;
  producto_nombre: string;
  establecimiento_nombre: string;
  kilos_primera: string;
  kilos_segunda: string;
  tarifa: string;
  importe_flete: string;
  transportista_nombre: string;
  producto_tipo: number;
}

// Interfaz para la respuesta de la API
interface ApiResponse {
  status: string;
  data: Ticket[];
  count?: number;
  message?: string;
}

interface EditableCellProps {
  editing: boolean;
  dataIndex: string;
  title: string;
  record: Ticket;
  index: number;
  children: React.ReactNode;
}

interface LiquidacionResponse {
  status: 'success' | 'error';
  message: string;
  id?: number;
  numero_liquidacion?: number;
  detalles_ids?: number[];
}

const FletesPendientes: React.FC = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, user } = useAuth0();
  const id_empresa = user?.id_empresa;
  const id_sucursal = user?.id_sucursal;
  const [transportistas, setTransportistas] = useState<Transportista[]>([]);
  const [selectedTransportista, setSelectedTransportista] = useState<number | null>(null);
  const [selectedTransportistaObj, setSelectedTransportistaObj] = useState<Transportista | null>(null);
  const [searchTransportista, setSearchTransportista] = useState('');
  const [showSearchTransportista, setShowSearchTransportista] = useState(false);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editingKey, setEditingKey] = useState<number>(-1);
  const [searchText, setSearchText] = useState('');
  const [filteredTickets, setFilteredTickets] = useState<Ticket[]>([]);
  const [canTrasladarTarifa, setCanTrasladarTarifa] = useState(false);
  const [ivaRate, setIvaRate] = useState<number>(21);
  const [tipoComprobante, setTipoComprobante] = useState<string>('FC');
  const [letraComprobante, setLetraComprobante] = useState<string>('A');
  const [numeroPuntoVenta, setNumeroPuntoVenta] = useState<string>('0000');
  const [numeroComprobante, setNumeroComprobante] = useState<string>('00000000');

  // Configuración de la selección
  const rowSelection: TableRowSelection<Ticket> = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
      // Habilitar B1 si hay exactamente un ítem seleccionado
      setCanTrasladarTarifa(newSelectedRowKeys.length === 1);
    },
  };

  const isEditing = (record: Ticket) => record.id === editingKey;

  const edit = (record: Ticket) => {
    setEditingKey(record.id);
  };

  const save = async (record: Ticket) => {
    try {
      setEditingKey(-1);
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  const cancel = () => {
    setEditingKey(-1);
  };

  const handleTarifaChange = (value: number | null, record: Ticket) => {
    const newTarifa = value || 0;
    const updatedTickets = tickets.map(ticket => {
      if (ticket.id === record.id) {
        return {
          ...ticket,
          tarifa: newTarifa.toString()
        };
      }
      return ticket;
    });
    setTickets(updatedTickets);
  };

  // Calcular totales de los tickets seleccionados
  const selectedTotals = useMemo(() => {
    const selected = tickets.filter(ticket => selectedRowKeys.includes(ticket.id));
    return selected.reduce((acc, ticket) => {
      const kilosPrimera = parseFloat(ticket.kilos_primera) || 0;
      const kilosSegunda = parseFloat(ticket.kilos_segunda) || 0;
      const kilos = Math.abs(kilosPrimera - kilosSegunda);
      const tarifa = parseFloat(ticket.tarifa) || 0;
      const importe = (tarifa * kilos) / 1000;

      return {
        kilos: acc.kilos + kilos,
        importe: acc.importe + importe
      };
    }, { kilos: 0, importe: 0 });
  }, [tickets, selectedRowKeys]);

  // Función de búsqueda global
  const handleSearch = (value: string) => {
    setSearchText(value);
    const filtered = tickets.filter(ticket => 
      Object.values(ticket).some(val => 
        val?.toString().toLowerCase().includes(value.toLowerCase())
      )
    );
    setFilteredTickets(filtered);
  };

  // Calcular el IVA
  const calculateIVA = (subtotal: number, rate: number) => {
    return (subtotal * rate) / 100;
  };

  // Calcular el Total
  const calculateTotal = (subtotal: number, rate: number) => {
    const iva = calculateIVA(subtotal, rate);
    return subtotal + iva;
  };

  const handlePuntoVentaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 4) {
      value = value.slice(0, 4);
    }
    setNumeroPuntoVenta(value);
  };

  const handleComprobanteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 8) {
      value = value.slice(0, 8);
    }
    setNumeroComprobante(value);
  };

  const handlePuntoVentaFocus = () => {
    setNumeroPuntoVenta('');
  };

  const handleComprobanteFocus = () => {
    setNumeroComprobante('');
  };

  const handlePuntoVentaBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value === '') {
      setNumeroPuntoVenta('0000');
    } else {
      value = value.padStart(4, '0');
      setNumeroPuntoVenta(value);
    }
  };

  const handleComprobanteBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value === '') {
      setNumeroComprobante('00000000');
    } else {
      value = value.padStart(8, '0');
      setNumeroComprobante(value);
    }
  };

  const handleGrabarLiquidacion = async () => {
    let liquidacionData = null; // Declaramos fuera del try para acceder en el catch

    try {
      // Verificar que el usuario esté autenticado y tenga los datos necesarios
      if (!user || !user.sub) {
        message.error('Error: Usuario no autenticado');
        return;
      }

      // Obtener los datos de empresa y sucursal del localStorage
      console.log('user=', user);

      if (!id_empresa || !id_sucursal) {
        message.error('Error: Datos de empresa o sucursal no disponibles');
        return;
      }

      const token = await getAccessTokenSilently();
      
      // Preparar los detalles de la liquidación
      const detalles = selectedRowKeys.map(key => {
        const ticket = tickets.find(t => t.id === key);
        return {
          balanza_id: ticket?.id,
          subtotal: parseFloat(ticket?.tarifa.toString() || '0')
        };
      });

      // Formatear el número de comprobante: letra + punto_venta + numero
      const numeroFormateado = `${letraComprobante}${numeroPuntoVenta}${numeroComprobante}`;

      liquidacionData = {
        transportista: selectedTransportista, 
        tipo_comprobante: tipoComprobante,        // Ejemplo: "FC"
        letra_comprobante: letraComprobante,      // Ejemplo: "A"
        numero_comprobante: numeroFormateado,     // Ejemplo: "A00010001234"
        fecha_comprobante: new Date().toISOString().split('T')[0],
        fecha_liquidacion: new Date().toISOString().split('T')[0],
        subtotal: parseFloat(selectedTotals.importe.toString()),
        iva: parseFloat(calculateIVA(selectedTotals.importe, ivaRate).toString()),
        tasa_iva: parseFloat(ivaRate.toString()),
        otros_impuestos: 0,
        numero_liquidacion: 0,
        id_empresa: parseInt(id_empresa),
        id_sucursal: parseInt(id_sucursal),
        alta: 1,
        detalles: detalles.map(d => ({
          balanza_id: d.balanza_id,
          subtotal: parseFloat(d.subtotal.toString())
        }))
      };

      console.log('Datos enviados al backend:', liquidacionData);

      // Mostrar loading mientras se procesa
      message.loading({ content: 'Procesando liquidación...', key: 'liquidacionLoading' });

      const response = await axios.post<LiquidacionResponse>(
        `${process.env.REACT_APP_BALANZA_URL}/registrar_liquidacion_fletes/`,
        liquidacionData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.status === 'success') {
        message.success({
          content: `Liquidación N° ${response.data.numero_liquidacion} grabada correctamente`,
          key: 'liquidacionLoading',
          duration: 3
        });
        
        // Limpiar selección
        setSelectedRowKeys([]);
        
        // Recargar los tickets para actualizar el estado
        if (selectedTransportista !== null) {
          await fetchTickets(selectedTransportista);
        } else {
          await fetchTickets(0); // O cualquier valor predeterminado que acepte tu función
        }
        
      } else {
        message.error({
          content: response.data.message || 'Error al grabar la liquidación',
          key: 'liquidacionLoading',
          duration: 3
        });
      }

    } catch (error: any) {
      console.error('Error completo:', error);
      console.error('Datos enviados:', liquidacionData);
      console.error('Respuesta del servidor:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.detail || 
                         `Error al grabar la liquidación: ${error.message}`;
      
      message.error({
        content: errorMessage,
        key: 'liquidacionLoading',
        duration: 3
      });
    }
  };

  // Calculamos si el botón debe estar habilitado
  const isB2Enabled = selectedRowKeys.length > 0;

  // Columnas de la tabla con validaciones
  const columns: ColumnType<Ticket>[] = [
    {
      title: 'Ticket',
      dataIndex: 'numero_ticket',
      key: 'numero_ticket',
      filteredValue: [searchText],
      filterIcon: <SearchOutlined />,
      sorter: (a, b) => a.numero_ticket.localeCompare(b.numero_ticket),
      render: (text: string) => text || '-'
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha_ticket',
      key: 'fecha_ticket',
      sorter: (a, b) => new Date(a.fecha_ticket).getTime() - new Date(b.fecha_ticket).getTime(),
      render: (text: string) => {
        if (!text) return '-';
        try {
          return new Date(text).toLocaleDateString();
        } catch (e) {
          return '-';
        }
      }
    },
    {
      title: 'Ingreso/Egreso',
      dataIndex: 'ingreso_egreso',
      key: 'ingreso_egreso',
      align: 'center' as const,
      render: (text: string) => {
        if (!text) return '-';
        return text === 'I' ? 'I' : 'E';
      }
    },
    {
      title: 'Cliente',
      dataIndex: 'cliente_nombre',
      key: 'cliente',
      filters: Array.from(new Set(tickets.map(t => t.cliente_nombre)))
        .map(cliente => ({ text: cliente, value: cliente })),
      onFilter: (value, record) => record.cliente_nombre === value,
      render: (text: string) => text || '-'
    },
    {
      title: 'Productor',
      dataIndex: 'productor_nombre',
      key: 'productor',
      render: (text: string) => text || '-'
    },
    {
      title: 'Chofer',
      dataIndex: 'chofer_nombre',
      key: 'chofer',
      render: (text: string) => text || '-'
    },
    {
      title: 'Camión',
      dataIndex: 'camion_patente',
      key: 'camion',
      render: (text: string) => text || '-'
    },
    {
      title: 'Producto',
      dataIndex: 'producto_nombre',
      key: 'producto',
      render: (text: string) => text || '-'
    },
    {
      title: 'Establecimiento',
      dataIndex: 'establecimiento_nombre',
      key: 'establecimiento',
      render: (text: string) => text || '-'
    },
    {
      title: 'Kilos',
      key: 'kilos',
      align: 'right' as const,
      render: (_: any, record: Ticket) => {
        try {
          const kilosPrimera = parseFloat(record.kilos_primera) || 0;
          const kilosSegunda = parseFloat(record.kilos_segunda) || 0;
          const diferencia = Math.abs(kilosPrimera - kilosSegunda);
          return diferencia.toLocaleString('es-AR');
        } catch (e) {
          return '-';
        }
      }
    },
    {
      title: 'Tarifa',
      dataIndex: 'tarifa',
      key: 'tarifa',
      align: 'right' as const,
      render: (value: string, record: Ticket) => {
        const editing = isEditing(record);
        return editing ? (
          <InputNumber
            defaultValue={parseFloat(value) || 0}
            onChange={(newValue) => handleTarifaChange(newValue, record)}
            onPressEnter={() => save(record)}
            onBlur={() => save(record)}
            style={{ width: '100%' }}
            min={0}
            step={0.01}
            formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, ''))}
            precision={2}
          />
        ) : (
          <div 
            style={{ cursor: 'pointer' }} 
            onClick={() => edit(record)}
          >
            {parseFloat(value || '0').toLocaleString('es-AR', { 
              style: 'currency', 
              currency: 'ARS' 
            })}
          </div>
        );
      }
    },
    {
      title: 'Importe',
      key: 'importe',
      align: 'right' as const,
      render: (_: any, record: Ticket) => {
        try {
          const kilosPrimera = parseFloat(record.kilos_primera) || 0;
          const kilosSegunda = parseFloat(record.kilos_segunda) || 0;
          const kilos = Math.abs(kilosPrimera - kilosSegunda);
          const tarifa = parseFloat(record.tarifa) || 0;
          
          const importe = (tarifa * kilos) / 1000;
          
          return importe.toLocaleString('es-AR', { 
            style: 'currency', 
            currency: 'ARS',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        } catch (e) {
          return '-';
        }
      }
    }
  ];

  // Cargar transportistas
  const fetchTransportistas = async () => {
    try {
      const token = await getAccessTokenSilently();
      
      if (!id_empresa) {
        message.error('Error: Datos de empresa no disponibles');
        return;
      }

      const response = await axios.get<Transportista[]>(
        `${process.env.REACT_APP_PADRON_URL}/leer_padron/${id_empresa}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log('Transportistas cargados:', response.data);
      setTransportistas(response.data);

    } catch (error) {
      console.error('Error al cargar transportistas:', error);
      message.error('Error al cargar lista de transportistas');
    }
  };

  // Cargar tickets
  const fetchTickets = async (transportistaId: number) => {
    try {
      setLoading(true);

      if (!id_empresa || !id_sucursal) {
        message.error('Error: Datos de empresa o sucursal no disponibles');
        return;
      }

      const token = await getAccessTokenSilently();
      const response = await axios.get<ApiResponse>(
        `${process.env.REACT_APP_BALANZA_URL}/listar_pendiente_fletes/${id_empresa}/${id_sucursal}/1/${transportistaId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === 'success' && Array.isArray(response.data.data)) {
        console.log('Tickets recibidos:', response.data.data);
        setTickets(response.data.data);
      } else {
        console.log('No hay datos de tickets o formato incorrecto');
        setTickets([]);
      }

    } catch (error) {
      console.error('Error al cargar tickets:', error);
      message.error('Error al cargar tickets pendientes');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  // Cargar transportistas al montar el componente
  useEffect(() => {
    fetchTransportistas();
  }, [user]);

  // Cerrar dropdown cuando se hace clic fuera del componente
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.transportista-search-container')) {
        setShowSearchTransportista(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Manejar cambio de transportista
  const handleTransportistaChange = (transportista: Transportista | null) => {
    console.log('Transportista seleccionado:', transportista);
    if (transportista) {
      setSelectedTransportista(transportista.id);
      setSelectedTransportistaObj(transportista);
      fetchTickets(transportista.id);
    } else {
      setSelectedTransportista(null);
      setSelectedTransportistaObj(null);
      fetchTickets(0);
    }
    setShowSearchTransportista(false);
  };

  const handleTrasladarTarifa = () => {
    const registroBase = tickets.find(ticket => ticket.id === selectedRowKeys[0]);
    
    if (!registroBase) {
      message.warning('Seleccione un registro primero');
      return;
    }

    Modal.confirm({
      title: 'Confirmar traslado de tarifa',
      icon: <ExclamationCircleFilled />,
      content: 'La tarifa de este registro se traslada a los registros donde coinciden Cliente, Productor, Producto y Establecimiento. ¿Está Seguro?',
      okText: 'Aceptar',
      cancelText: 'Cancelar',
      onOk() {
        const updatedTickets = tickets.map(ticket => {
          if (ticket.cliente_nombre === registroBase.cliente_nombre &&
              ticket.productor_nombre === registroBase.productor_nombre &&
              ticket.producto_nombre === registroBase.producto_nombre &&
              ticket.establecimiento_nombre === registroBase.establecimiento_nombre) {
            return {
              ...ticket,
              tarifa: registroBase.tarifa
            };
          }
          return ticket;
        });

        setTickets(updatedTickets);
        setSelectedRowKeys([]);
        setCanTrasladarTarifa(false);
        message.success('Tarifas actualizadas correctamente');
      },
      onCancel() {
        message.info('Operación cancelada');
      },
    });
  };

  const containerRef = useRef<HTMLDivElement>(null);

  // Agregar animación de entrada al montar el componente
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.classList.add('slide-in-right');
      // Remover la clase después de la animación
      const timer = setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.classList.remove('slide-in-right');
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div ref={containerRef} className="page-transition-container" style={{ padding: '0', width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      <style>{`
        .page-transition-container {
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
          transform: translateX(0);
          opacity: 1;
        }
        .page-transition-container.slide-out-left {
          transform: translateX(-100%);
          opacity: 0;
        }
        .page-transition-container.slide-in-right {
          animation: slideInRight 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
      {/* Título de la página */}
      <h1 style={{ 
        color: theme.colors.primaryDark, 
        fontSize: '28px', 
        fontWeight: 600, 
        marginBottom: '24px' 
      }}>
        Pendientes de Fletes
      </h1>

      <Card 
        style={{ 
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow,
          maxWidth: '100%'
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '16px',
            fontWeight: 600
          }
        }}
        title="Fletes Pendientes de Liquidar"
      >
      <div style={{ padding: '24px' }}>
        {/* Filtros */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '20px',
          marginBottom: '24px',
          alignItems: 'end'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: 600,
              color: theme.colors.textPrimary,
              fontSize: '14px'
            }}>
              Transportista:
            </label>
            <div className="transportista-search-container" style={{ position: 'relative', width: '100%' }}>
              <Input
                placeholder="Buscar por NC o denominación..."
                size="large"
                prefix={<SearchOutlined />}
                value={selectedTransportistaObj ? `${selectedTransportistaObj.nc} - ${selectedTransportistaObj.denominacion}` : searchTransportista}
                onChange={e => {
                  setSearchTransportista(e.target.value);
                  setSelectedTransportistaObj(null);
                  setShowSearchTransportista(true);
                }}
                onFocus={() => setShowSearchTransportista(true)}
                allowClear={!!selectedTransportistaObj}
                onClear={() => {
                  handleTransportistaChange(null);
                  setSearchTransportista('');
                }}
                style={{
                  backgroundColor: selectedTransportistaObj ? theme.colors.backgroundGray : 'white',
                  cursor: 'pointer'
                }}
              />
              {/* Dropdown */}
              {showSearchTransportista && transportistas.length > 0 && !selectedTransportistaObj && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  {transportistas.filter(t => {
                    const search = searchTransportista.trim().toLowerCase();
                    return (
                      t.nc.toLowerCase().includes(search) ||
                      t.denominacion.toLowerCase().includes(search)
                    );
                  }).map(transportista => (
                    <div
                      key={transportista.id}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                        textAlign: 'left'
                      }}
                      onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                      onMouseOut={e => (e.currentTarget.style.backgroundColor = 'white')}
                      onClick={() => {
                        handleTransportistaChange(transportista);
                        setSearchTransportista('');
                      }}
                    >
                      {transportista.nc} - {transportista.denominacion}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: 600,
              color: theme.colors.textPrimary,
              fontSize: '14px'
            }}>
              Búsqueda Global:
            </label>
            <Input.Search
              placeholder="Buscar en todas las columnas"
              allowClear
              onSearch={handleSearch}
              size="large"
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <div style={{
          width: '100%',
          overflowX: 'auto'  // Contenedor con scroll para la tabla
        }}>
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={searchText ? filteredTickets : tickets}
            loading={loading}
            rowKey="id"
            pagination={{ 
              pageSize: 10,
              total: tickets.length 
            }}
            scroll={{ 
              x: 'max-content',  // Permite que la tabla se expanda horizontalmente
              scrollToFirstRowOnChange: true
            }}
            size="small"
            bordered
            style={{
              minWidth: '100%'  // Asegura que la tabla ocupe al menos todo el ancho
            }}
          />
        </div>

        {/* Botón Cambio Tarifa */}
        <div style={{ 
          marginTop: '20px',
          display: 'flex',
          justifyContent: 'flex-start'
        }}>
          <Button
            type="primary"
            icon={<CopyOutlined />}
            onClick={handleTrasladarTarifa}
            size="large"
            disabled={!canTrasladarTarifa}
            style={{
              backgroundColor: theme.colors.secondary,
              borderColor: theme.colors.secondary,
              fontWeight: 500,
              minWidth: '180px',
              opacity: canTrasladarTarifa ? 1 : 0.6
            }}
          >
            Cambio Tarifa
          </Button>
        </div>

        {/* Cuadro Resumen */}
        <Card
          style={{
            marginTop: '24px',
            backgroundColor: '#ffffff',
            borderRadius: theme.styles.borderRadius,
            boxShadow: theme.styles.boxShadow,
            maxWidth: '800px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}
          styles={{
            header: {
              backgroundColor: theme.colors.primaryDark,
              color: 'white',
              borderRadius: '8px 8px 0 0',
              fontSize: '16px',
              fontWeight: 600,
              textAlign: 'center'
            }
          }}
          title="Resumen Liquidación"
        >
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            marginBottom: '16px'
          }}>
            <div>
              <span style={{ color: theme.colors.textSecondary, fontSize: '14px' }}>Cantidad de Registros Tildados:</span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontWeight: 600, fontSize: '16px', color: theme.colors.textPrimary }}>{selectedRowKeys.length}</span>
            </div>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            marginBottom: '16px'
          }}>
            <div>
              <span style={{ color: theme.colors.textSecondary, fontSize: '14px' }}>Subtotal:</span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontWeight: 600, fontSize: '16px', color: theme.colors.textPrimary }}>
                {selectedTotals.importe.toLocaleString('es-AR', {
                  style: 'currency',
                  currency: 'ARS',
                  minimumFractionDigits: 2
                })}
              </span>
            </div>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            marginBottom: '16px',
            alignItems: 'center'
          }}>
            <div>
              <span style={{ color: theme.colors.textSecondary, fontSize: '14px' }}>IVA:</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-end' }}>
              <Select
                value={ivaRate}
                onChange={(value: number) => setIvaRate(value)}
                size="large"
                style={{ width: 100 }}
              >
                <Option value={0}>0%</Option>
                <Option value={10.5}>10.5%</Option>
                <Option value={21}>21%</Option>
                <Option value={27}>27%</Option>
              </Select>
              <span style={{ fontWeight: 600, fontSize: '16px', color: theme.colors.textPrimary }}>
                {calculateIVA(selectedTotals.importe, ivaRate).toLocaleString('es-AR', {
                  style: 'currency',
                  currency: 'ARS',
                  minimumFractionDigits: 2
                })}
              </span>
            </div>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            marginBottom: '16px',
            paddingTop: '16px',
            borderTop: `1px solid ${theme.colors.borderLight}`
          }}>
            <div>
              <span style={{ fontWeight: 600, fontSize: '16px', color: theme.colors.textPrimary }}>Total:</span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontWeight: 700, fontSize: '18px', color: theme.colors.primary }}>
                {calculateTotal(selectedTotals.importe, ivaRate).toLocaleString('es-AR', {
                  style: 'currency',
                  currency: 'ARS',
                  minimumFractionDigits: 2
                })}
              </span>
            </div>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            marginBottom: '16px',
            alignItems: 'center'
          }}>
            <div>
              <span style={{ color: theme.colors.textSecondary, fontSize: '14px' }}>Comprobante:</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
              <Select
                value={tipoComprobante}
                onChange={(value: string) => setTipoComprobante(value)}
                size="large"
                style={{ width: 100 }}
              >
                <Option value="RT">RT</Option>
                <Option value="FC">FC</Option>
              </Select>
              <Select
                value={letraComprobante}
                onChange={(value: string) => setLetraComprobante(value)}
                size="large"
                style={{ width: 80 }}
              >
                <Option value="X">X</Option>
                <Option value="C">C</Option>
                <Option value="A">A</Option>
              </Select>
              <Input
                value={numeroPuntoVenta}
                onChange={handlePuntoVentaChange}
                onFocus={handlePuntoVentaFocus}
                onBlur={handlePuntoVentaBlur}
                size="large"
                style={{ width: 80 }}
                maxLength={4}
              />
              <Input
                value={numeroComprobante}
                onChange={handleComprobanteChange}
                onFocus={handleComprobanteFocus}
                onBlur={handleComprobanteBlur}
                size="large"
                style={{ width: 120 }}
                maxLength={8}
              />
            </div>
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: `1px solid ${theme.colors.borderLight}`
          }}>
            <Button
              type="primary"
              onClick={handleGrabarLiquidacion}
              disabled={!isB2Enabled}
              size="large"
              style={{
                backgroundColor: theme.colors.secondary,
                borderColor: theme.colors.secondary,
                fontWeight: 500,
                minWidth: '200px',
                opacity: isB2Enabled ? 1 : 0.6
              }}
            >
              Grabar
            </Button>
          </div>
        </Card>
      </div>
      </Card>
    </div>
  );
};

export default FletesPendientes; 